import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import fetchSalon from 'actions/fetchSalon.action';
import ICO_SETTINGS from 'assets/images/icons/settings-black.svg';
import CheckboxInput from 'components/form/CheckboxInput.component';
import Foldout from 'components/generic/Foldout.component';
import OverlaySpinner from 'components/generic/OverlaySpinner.component';
import TooltipForReceptionist from 'components/TooltipForReceptionist';
import DELAY from 'constants/delay.const';
import IN from 'constants/in';
import { useTranslation } from 'providers/GILT.provider';
import { selectCurrentSalonId } from 'selectors/currentSalon.selectors';
import delayed from 'utils/functions/delayed.util';
import logger from 'utils/logger.util';

import { hideNonWorking, restoreServerData, setSavedFalse, toggleRequestDepositPrompt, updateSalonDetails } from 'settings/noshowvisibility/actions';
import SectionContent from 'settings/online-booking/pages/SectionContent.component';
import SectionSeparator from 'settings/online-booking/pages/SectionSeparator.component';
import SectionTitle from 'settings/online-booking/pages/SectionTitle.component';
import Button from 'settings/page-elements/Button';
import ColorModeLayout from 'settings/page-elements/no-show-visibility/ColorModeLayout.component';

const L = logger('SetUpView');

const FLAG_DISPLAY_ELM = false;

const SetUpView = (props) => {
  const { salonId, currentSalon, noshow_behaviour: fromProps, isFetching, fetchSalon, setSavedFalse, restoreServerData, hideNonWorking, toggleRequestDepositPrompt, updateSalonDetails } = props;

  const [alertRequestDeposit, setAlertRequestDeposit] = useState(false);
  const [noshowBehaviour, setNoshowBehaviour] = useState(fromProps);
  const [hideNonWorkingState, setHideNonWorkingState] = useState(currentSalon?.hide_non_working_stylist_column);
  const [requestDepositPrompt, setRequestDepositPrompt] = useState(currentSalon?.request_deposit_prompt);
  const [hasStripeAccount] = useState(currentSalon?.has_ncob_stripe_account);

  const { t } = useTranslation();

  useEffect(() => {
    if (currentSalon) {
      setHideNonWorkingState(currentSalon?.hide_non_working_stylist_column);
      setRequestDepositPrompt(currentSalon?.request_deposit_prompt);
    }
  }, [currentSalon]);
  // FIXME: use better way and remove hidden button
  const onSalonCheckBox = useCallback(() => {
    const button = document.querySelector('.save-no-show-visibility');
    const isShowInView = noshowBehaviour === 'show_in_view';
    const isUndecided = noshowBehaviour === 'undecided';

    if (isShowInView || isUndecided) {
      setNoshowBehaviour('hide_in_view');
      if (button) delayed(DELAY.attention, () => button.click());
      setSavedFalse();
    } else {
      if (noshowBehaviour === 'hide_in_view') {
        if (button) delayed(DELAY.attention, () => button.click());
        restoreServerData();
      }
      setNoshowBehaviour('show_in_view');
    }

    setSavedFalse();
  }, [noshowBehaviour, setSavedFalse, restoreServerData]);

  const onShowHideNonWorking = useCallback(() => {
    const value = hideNonWorkingState ? 'False' : 'True';
    L.unignore('onShowHideNonWorking()', 'hideNonWorking:', hideNonWorking(salonId, value));

    delayed(DELAY.attention, () => L.unignore('onShowHideNonWorking()', 'fetchSalon:', fetchSalon(salonId)));
  }, [hideNonWorkingState, hideNonWorking, fetchSalon, salonId]);

  const onCheckboxRequestDeposit = useCallback(() => {
    const value = requestDepositPrompt ? 'False' : 'True';
    L.unignore('onCheckboxRequestDeposit()', 'toggleRequestDepositPrompt:', toggleRequestDepositPrompt(salonId, value));

    delayed(DELAY.attention, () => {
      L.unignore('onCheckboxRequestDeposit()', 'fetchSalon:', fetchSalon(salonId));
      setAlertRequestDeposit(true);
    });
  }, [requestDepositPrompt, toggleRequestDepositPrompt, fetchSalon, salonId]);

  const saveChanges = useCallback(() => {
    const value = { ...currentSalon, noshow_behaviour: noshowBehaviour };
    L.unignore('saveChanges()', 'updateSalonDetails:', updateSalonDetails(value));
  }, [currentSalon, noshowBehaviour, updateSalonDetails]);

  const isShowInView = noshowBehaviour === 'show_in_view';
  const isUndecided = noshowBehaviour === 'undecided';

  return (
    <div data-bem="SetupView--calendar-settings">
      {alertRequestDeposit && <div className="alert success animated fadeInDown">{t('staffAndBusiness.calendarSettings.saved')}</div>}

      <ColorModeLayout tag="SetUpView__salon--mode" />

      <Foldout tag="SetUpView__calendar--settings" src={ICO_SETTINGS} title={t('staffAndBusiness.calendarSettings.title2')} width="lg" isOpen>
        <SectionSeparator />
        <SectionTitle value={t('staffAndBusiness.calendarSettings.subtitle1')}>{t('staffAndBusiness.calendarSettings.nonWorkingStaffDescription')}</SectionTitle>
        <SectionContent>
          <div className="relative">
            <CheckboxInput variant="tick" checked={hideNonWorkingState} onClick={onShowHideNonWorking}>
              {t('staffAndBusiness.calendarSettings.hideNonWorkingCheckbox')}
            </CheckboxInput>
            <TooltipForReceptionist where={IN.noShow} />
          </div>
        </SectionContent>

        <SectionSeparator />
        <SectionTitle value={t('staffAndBusiness.calendarSettings.subtitle2')}>{t('staffAndBusiness.calendarSettings.noShowDescription')}</SectionTitle>
        <SectionContent>
          <div className="relative">
            <CheckboxInput variant="tick" checked={isShowInView || isUndecided} onClick={onSalonCheckBox}>
              {t('staffAndBusiness.calendarSettings.keepNoShowCheckbox')}
            </CheckboxInput>
            <TooltipForReceptionist where={IN.noShow} />
          </div>
          {!isFetching && <Button buttonName={t('staffAndBusiness.calendarSettings.saveNoShowButton')} onClickButton={saveChanges} className="save-no-show-visibility hidden" />}
        </SectionContent>
        <SectionSeparator />

        {FLAG_DISPLAY_ELM && (
          <>
            <SectionTitle value={t('staffAndBusiness.calendarSettings.subtitle3')}></SectionTitle>
            <SectionContent>
              {hasStripeAccount && (
                <CheckboxInput variant="tick" checked={requestDepositPrompt} onClick={onCheckboxRequestDeposit}>
                  {t('staffAndBusiness.calendarSettings.keepRequestOpenCheckbox')}
                </CheckboxInput>
              )}
            </SectionContent>
          </>
        )}
      </Foldout>
      {isFetching && <OverlaySpinner />}
    </div>
  );
};

const mapStateToProps = (state) => ({
  salonId: selectCurrentSalonId(state),
  currentSalon: state?.currentSalon,
  noshow_behaviour: state?.currentSalon?.noshow_behaviour,
  isFetching: state?.settings?.noshowVisibility?.isFetching,
  noShowBehaviourIsSaved: state?.settings?.noshowVisibility?.noShowBehaviourIsSaved,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      updateSalonDetails,
      setSavedFalse,
      restoreServerData,
      hideNonWorking,
      toggleRequestDepositPrompt,
      fetchSalon,
    },
    dispatch,
  );

const SetUpViewHoc = connect(mapStateToProps, mapDispatchToProps)(SetUpView);

export default SetUpViewHoc;
